/**
 * MapLoadingOverlay - Non-blocking loading indicator for GeoTIFF Viewer
 *
 * Displays polygon pipeline status as a semi-transparent overlay INSIDE the map,
 * allowing the map to be visible and interactive while data loads.
 *
 * Part of Phase 1: Non-Blocking Loading Pattern
 */

'use client';

import React from 'react';

export type PolygonPipelineStatus = 'pending' | 'fetching' | 'complete' | 'error';

interface MapLoadingOverlayProps {
  /** Current status of the polygon pipeline */
  status: PolygonPipelineStatus;
  /** Optional cache ID to display */
  cacheId?: string | null;
  /** Whether the overlay is visible */
  visible?: boolean;
  /** Position of the overlay */
  position?: 'center' | 'bottom-right' | 'top-right';
}

/**
 * Semi-transparent overlay showing polygon pipeline status.
 * Renders inside the map container without blocking interaction.
 */
export function MapLoadingOverlay({
  status,
  cacheId,
  visible = true,
  position = 'center',
}: MapLoadingOverlayProps) {
  if (!visible || status === 'complete') {
    return null;
  }

  // Position classes based on prop
  const positionClasses = {
    center: 'inset-0 flex items-center justify-center',
    'bottom-right': 'bottom-4 right-4',
    'top-right': 'top-4 right-4',
  };

  // Determine if we should show full overlay (center) or compact card (corner)
  const isFullOverlay = position === 'center';

  return (
    <div
      className={`absolute ${positionClasses[position]} z-[1000] pointer-events-none`}
      style={{ pointerEvents: 'none' }}
    >
      {/* Semi-transparent backdrop only for center position */}
      {isFullOverlay && (
        <div className="absolute inset-0 bg-black/20" />
      )}

      {/* Loading card */}
      <div
        className={`
          relative bg-white/95 backdrop-blur-sm rounded-lg shadow-lg border-2 border-gray-200
          ${isFullOverlay ? 'p-6 max-w-sm' : 'p-3'}
        `}
      >
        {/* Spinner */}
        <div className={`flex items-center ${isFullOverlay ? 'flex-col' : 'flex-row gap-3'}`}>
          <div
            className={`
              animate-spin rounded-full border-b-2 border-[#EBFD03]
              ${isFullOverlay ? 'h-10 w-10 mb-4' : 'h-6 w-6'}
            `}
          />

          <div className={isFullOverlay ? 'text-center' : ''}>
            {isFullOverlay && (
              <p className="text-gray-800 font-semibold mb-3">
                {status === 'pending' && 'Preparing map...'}
                {status === 'fetching' && 'Loading roof data...'}
                {status === 'error' && 'Error loading data'}
              </p>
            )}

            {/* Pipeline Status */}
            <div className={`${isFullOverlay ? 'text-left bg-gray-50 rounded-lg p-3' : ''}`}>
              {isFullOverlay && (
                <p className="font-bold text-xs text-gray-700 mb-2 uppercase tracking-wide">
                  Polygon Pipeline
                </p>
              )}

              <div className={`space-y-1 ${isFullOverlay ? 'text-xs' : 'text-[10px]'}`}>
                <StatusLine
                  label="Initializing"
                  active={status === 'pending'}
                  completed={status === 'fetching' || status === 'error'}
                  compact={!isFullOverlay}
                />
                <StatusLine
                  label="Extracting polygons"
                  active={status === 'fetching'}
                  completed={false}
                  compact={!isFullOverlay}
                />
                {status === 'error' && (
                  <p className="text-red-600 font-semibold">
                    Error - Check console
                  </p>
                )}
              </div>

              {/* Cache ID */}
              {cacheId && isFullOverlay && (
                <p className="text-[10px] text-gray-400 mt-2 font-mono truncate">
                  Cache: {cacheId.substring(0, 20)}...
                </p>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

/**
 * Individual status line with icon
 */
function StatusLine({
  label,
  active,
  completed,
  compact = false,
}: {
  label: string;
  active: boolean;
  completed: boolean;
  compact?: boolean;
}) {
  const getStatusIcon = () => {
    if (completed) return '\u2713'; // checkmark
    if (active) return '\u2026';    // ellipsis (animating)
    return '\u2022';                // bullet
  };

  const getStatusClass = () => {
    if (completed) return 'text-green-600';
    if (active) return 'text-blue-600 font-semibold animate-pulse';
    return 'text-gray-400';
  };

  if (compact && !active) {
    return null; // In compact mode, only show active step
  }

  return (
    <p className={getStatusClass()}>
      <span className="inline-block w-4">{getStatusIcon()}</span>
      {compact ? label : (active ? `${label}...` : label)}
    </p>
  );
}

export default MapLoadingOverlay;
